/**
 * Chain configuration for Hupper.
 *
 * Active chain is selected via VITE_CHAIN env var:
 *   - "anvil"   → local dev (default for dev mode)
 *   - "amoy"    → Polygon Amoy testnet
 *   - "polygon" → Polygon mainnet (production)
 *
 * Vault, USDT, Games and USDC addresses are loaded from
 * `packages/contracts/deployments/<chain>.json`. Env vars override when
 * present (`VITE_HUPPER_GAMES_ADDRESS`, `VITE_USDC_ADDRESS`,
 * `VITE_HUPPER_VAULT_ADDRESS`).
 */
import anvilDeployment from "../../../../packages/contracts/deployments/anvil-local.json";
import amoyDeployment from "../../../../packages/contracts/deployments/amoy.json";

export type ChainKey = "anvil" | "amoy" | "polygon";

export interface ChainConfig {
  key: ChainKey;
  chainId: number;
  name: string;
  rpcUrl: string;
  explorerUrl: string | null;
  nativeSymbol: string;
  /** HupperVault (legacy — Pong). */
  vaultAddress: string | null;
  /** ERC20 used by HupperVault (USDT on Polygon mainnet, MockUSDT on Amoy/Anvil). */
  usdtAddress: string | null;
  /** Legacy paid-mode flag for HupperVault. */
  paidMode: boolean;
  /** HupperGames (Polymarket-style escrow — Snake first). */
  gamesAddress: string | null;
  /** ERC20 used by HupperGames (USDC; on Amoy reuses MockUSDT). */
  usdcAddress: string | null;
}

const envAddr = (v: string | undefined) => (v && v !== "" ? v : null);

const CHAINS: Record<ChainKey, ChainConfig> = {
  anvil: {
    key: "anvil",
    chainId: 31337,
    name: "Anvil (local)",
    rpcUrl: "http://localhost:8545",
    explorerUrl: null,
    nativeSymbol: "ETH",
    vaultAddress: anvilDeployment.contracts.HupperVault.address,
    usdtAddress: anvilDeployment.contracts.MockUSDT.address,
    paidMode: false,
    // Anvil has no HupperGames deploy by default; rely on env vars if a
    // local deploy script populates one.
    gamesAddress: envAddr(import.meta.env.VITE_HUPPER_GAMES_ADDRESS),
    usdcAddress:
      envAddr(import.meta.env.VITE_USDC_ADDRESS) ??
      anvilDeployment.contracts.MockUSDT.address,
  },
  amoy: {
    key: "amoy",
    chainId: 80002,
    name: "Polygon Amoy",
    rpcUrl: import.meta.env.VITE_AMOY_RPC ?? "https://rpc-amoy.polygon.technology",
    explorerUrl: "https://amoy.polygonscan.com",
    nativeSymbol: "POL",
    vaultAddress:
      envAddr(import.meta.env.VITE_HUPPER_VAULT_ADDRESS) ??
      amoyDeployment.contracts.HupperVault.address,
    usdtAddress: amoyDeployment.contracts.MockUSDT.address,
    paidMode: amoyDeployment.config.HupperVault.paidMode,
    gamesAddress:
      envAddr(import.meta.env.VITE_HUPPER_GAMES_ADDRESS) ??
      amoyDeployment.contracts.HupperGames?.address ??
      null,
    usdcAddress:
      envAddr(import.meta.env.VITE_USDC_ADDRESS) ??
      amoyDeployment.contracts.MockUSDT.address,
  },
  polygon: {
    key: "polygon",
    chainId: 137,
    name: "Polygon",
    rpcUrl: import.meta.env.VITE_POLYGON_RPC ?? "https://polygon-rpc.com",
    explorerUrl: "https://polygonscan.com",
    nativeSymbol: "POL",
    vaultAddress: envAddr(import.meta.env.VITE_HUPPER_VAULT_ADDRESS),
    usdtAddress: "0xc2132D05D31c914a87C6611C10748AEb04B58e8F", // bridged USDT (PoS)
    paidMode: true,
    gamesAddress: envAddr(import.meta.env.VITE_HUPPER_GAMES_ADDRESS),
    usdcAddress:
      envAddr(import.meta.env.VITE_USDC_ADDRESS) ??
      "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359", // native USDC
  },
};

const ENV_CHAIN = (import.meta.env.VITE_CHAIN ?? "anvil") as ChainKey;
if (!CHAINS[ENV_CHAIN]) {
  // eslint-disable-next-line no-console
  console.warn(`[chains] unknown VITE_CHAIN="${ENV_CHAIN}", falling back to anvil`);
}

export const ACTIVE_CHAIN: ChainConfig = CHAINS[ENV_CHAIN] ?? CHAINS.anvil;
export const ALL_CHAINS = CHAINS;

export function isVaultDeployed(chain: ChainConfig = ACTIVE_CHAIN): boolean {
  return Boolean(chain.vaultAddress && chain.usdtAddress);
}

export function isGamesDeployed(chain: ChainConfig = ACTIVE_CHAIN): boolean {
  return Boolean(chain.gamesAddress && chain.usdcAddress);
}
